import propz from 'propz';
import { AppUser } from 'Src/views/App/App';
import { getParentInboxMessagesCount, getParentArchiveMessagesCount } from 'Src/helpers/service/parent/messagesTable';
import {
  getStudentInboxMessagesCount,
  getStudentArchiveMessagesCount
} from 'Src/helpers/service/student/messagesTable';
import { loadParentClubProgrammesData } from 'Src/views/GenericView/ParentView/ClubProgrammes/ParentClubProgrammes.loader';
import { ParentClubProgrammesLoadResult } from 'Src/views/GenericView/ParentView/ClubProgrammes/ParentClubProgrammes.loader';
import { Child } from 'Src/models/child';
import { getStudentProgrammeBookingInbox } from 'Src/helpers/service/student/clubProgrammesBooking';

export interface ClubSectionCounts {
  invitations: number;
  browse: number;
  archive: number;
}
export const hasClubSectionContent = (counts?: ClubSectionCounts) =>
  !counts || counts.invitations > 0 || counts.browse > 0 || counts.archive > 0;
export const currentAcademicYearRange = () => {
  const now = new Date();
  const startYear = now.getMonth() >= 8 ? now.getFullYear() : now.getFullYear() - 1;
  return { from: `${startYear}-09-01`, to: `${startYear + 1}-08-31` };
};
const CLUB_WHERE = { kind: 'ClubParticipantInviteMessage' };

interface KnownParentClubSectionData {
  invitations?: number;
  archive?: number;
  programmes?: ParentClubProgrammesLoadResult;
  children?: Child[];
}

export async function getParentClubSectionCounts(
  user: AppUser,
  known: KnownParentClubSectionData = {}
): Promise<ClubSectionCounts> {
  const [inbox, archive, programmes] = await Promise.all([
    typeof known.invitations === 'number'
      ? Promise.resolve({ count: known.invitations })
      : getParentInboxMessagesCount(user, CLUB_WHERE),
    typeof known.archive === 'number'
      ? Promise.resolve({ count: known.archive })
      : getParentArchiveMessagesCount(user, CLUB_WHERE),
    known.programmes ? Promise.resolve(known.programmes) : loadParentClubProgrammesData(user, known.children)
  ]);
  const programmeIds = new Set((programmes.flatItems || []).map(item => String(item.id || '')).filter(Boolean));
  return { invitations: Number(inbox.count) || 0, archive: Number(archive.count) || 0, browse: programmeIds.size };
}

export async function getStudentClubSectionCounts(
  user: AppUser,
  known: { browse?: number } = {}
): Promise<ClubSectionCounts> {
  const permission = propz.get(user, ['activePermission'], null);
  const schoolId = String(propz.get(permission, ['schoolId'], '') || '');
  const permissionId = String(propz.get(permission, ['id'], propz.get(permission, ['_id'], '')) || '');
  const [inbox, archive, programmes] = await Promise.all([
    getStudentInboxMessagesCount(user, CLUB_WHERE),
    getStudentArchiveMessagesCount(user, CLUB_WHERE),
    typeof known.browse === 'number'
      ? Promise.resolve(new Array(known.browse))
      : schoolId
      ? getStudentProgrammeBookingInbox(user, schoolId, permissionId)
      : Promise.resolve([])
  ]);
  return {
    invitations: Number(inbox.count) || 0,
    archive: Number(archive.count) || 0,
    browse: (programmes || []).length
  };
}

export function clubSectionTabs(
  counts: ClubSectionCounts | undefined,
  active: 'invitations' | 'browse' | 'pastCurrent' | 'pastPrevious',
  history: any
) {
  const resolvedCounts = counts || { invitations: 0, browse: 0, archive: 0 };
  return [
    { key: 'invitations', label: 'Invitations', count: resolvedCounts.invitations, path: '/clubBookings/inbox' },
    { key: 'browse', label: 'Browse & book', count: resolvedCounts.browse, path: '/clubProgrammes' },
    { key: 'pastCurrent', label: 'Past this year', count: resolvedCounts.archive, path: '/clubBookings/history' },
    { key: 'pastPrevious', label: 'Past previous years', count: resolvedCounts.archive, path: '/clubBookings/archive' }
  ]
    .filter(tab => tab.key !== 'browse' || tab.count > 0 || active === 'browse')
    .map(tab => ({
      label: tab.label,
      count: tab.key === 'invitations' || tab.key === 'browse' ? tab.count : undefined,
      active: tab.key === active,
      onClick: () => history.push(tab.path)
    }));
}
